import React, { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Eye, HeartPulse, ChevronRight } from "lucide-react";
import { authAPI } from "../../services/api";
import cacheService from "../../services/cacheService";

import crop2 from "../../assets/logos/crop2.png";
import WeatherWidget from "./dashboardComponents/weatherWidget";
import Calendar from "./dashboardComponents/calender";

export default function DashboardHome() {
  const [userData, setUserData] = useState(null);
  // Fetch user data from localStorage
  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    setUserData(user);
  }, []);
  // Get user name
  const getUserName = () => {
    if (userData && userData.first_name && userData.last_name) {
      return `${userData.first_name} ${userData.last_name}`;
    }
    return "User";
  };
  const farmEvents = {
    "2025-4-5": "planting",
    "2025-4-15": "inspection",
  };

  const [activeMenu, setActiveMenu] = useState(null);
  const menuRefs = useRef({});

  // Dashboard data states
  const [dashboardStats, setDashboardStats] = useState({
    totalProducts: 0,
    totalOrders: 0,
    totalIncome: 0,
    totalExpenses: 0
  });

  const [produce, setProduce] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch dashboard data
  useEffect(() => {
    fetchDashboardData();

    // Listen for when user returns to the page to refresh data
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        fetchDashboardData(true); // Force refresh
      }
    };

    const handleFocus = () => {
      fetchDashboardData(true); // Force refresh
    };

    // Add event listeners
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    // Cleanup
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event) {
      if (activeMenu) {
        const menuElement = menuRefs.current[activeMenu];
        const toggleElement = document.getElementById(`toggle-${activeMenu}`);

        if (
          menuElement &&
          !menuElement.contains(event.target) &&
          (!toggleElement || !toggleElement.contains(event.target))
        ) {
          setActiveMenu(null);
        }
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeMenu]);

  const fetchDashboardData = async (forceRefresh = false) => {
    try {
      setLoading(true);
      setError(null);

      // Set dummy statistics for now
      setDashboardStats({
        totalProducts: 0,
        totalOrders: 0,
        totalIncome: 0,
        totalExpenses: 0
      });

      // Clear cache if force refresh is requested
      if (forceRefresh) {
        // Clear localStorage cache
        localStorage.removeItem('dashboardData');
        localStorage.removeItem('produceData');
        localStorage.removeItem('ordersData');

        // Clear any cached API responses
        try {
          await cacheService.clearAllCache();
        } catch (error) {
          console.warn('Failed to clear cache:', error);
        }
      }

      // Fetch farm management data (produce) - this is the correct endpoint
      const farmData = await authAPI.getFarmManagement();

      // Set produce data
      setProduce(farmData.data || []);

    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      setError('Failed to load produce data');
    } finally {
      setLoading(false);
    }
  };

  const toggleProductMenu = (productId, event) => {
    event.stopPropagation();
    setActiveMenu(activeMenu === productId ? null : productId);
  };

  // Helper function to get current and previous stages
  const getStages = (growingStages) => {
    if (!growingStages || growingStages.length === 0) {
      return { currentStage: "Not Started", prevStage: "N/A" };
    }

    const currentStage = growingStages.find(stage => stage.is_current);
    const currentIndex = growingStages.findIndex(stage => stage.is_current);

    if (currentStage) {
      const prevStage = currentIndex > 0 ? growingStages[currentIndex - 1] : null;
      return {
        currentStage: currentStage.stage_name,
        prevStage: prevStage ? prevStage.stage_name : "Not Started"
      };
    } else {
      // If no current stage, use the last stage
      const lastStage = growingStages[growingStages.length - 1];
      return {
        currentStage: lastStage.stage_name,
        prevStage: growingStages.length > 1 ? growingStages[growingStages.length - 2].stage_name : "Not Started"
      };
    }
  };

  // Helper function to get product image
  const getProductImage = (imageUrl) => {
    if (imageUrl && imageUrl.startsWith('http')) {
      return imageUrl;
    }
    // Fallback to dummy images based on product name or use a default
    return crop2; // Default fallback
  };

  const ProductMenu = ({ productId }) => (
    <div
      className="productmenu"
      ref={(el) => (menuRefs.current[productId] = el)}
    >
      <Link to={`/farmer-dashboard/produce/${productId}`}>
        <Eye size={16} />
        View
      </Link>
      <Link to={`/farmer-dashboard/myfarm/manage/${productId}`}>
        <HeartPulse size={18} />
        Manage
      </Link>
    </div>
  );

  return (
    <>
      <div className="dash-content-header">
        <div className="header-txt">
          <h1>Dashboard</h1>
          <p>Welcome back, {getUserName()}</p>
        </div>
        <div className="header-cta-buttons">
          <Link to={"/farmer-dashboard/sell"}>List a Product</Link>
        </div>
      </div>

      <div className="dash-top-section">
        <div className="dash-lef">
          <div className="dash-card-grid">
            <div className="dash-card">
              <div className="dash-card-header">
                <span>
                  <i className="fas fa-box"></i>
                  <h2>Total Products</h2>
                </span>

              </div>
              <div className="dash-card-body">
                <h2>{loading ? "..." : `${dashboardStats.totalProducts}+`}</h2>
              </div>
            </div>
            <div className="dash-card">
              <div className="dash-card-header">
                <span>
                  <i className="fas fa-shopping-cart"></i>
                  <h2>Total Orders</h2>
                </span>

              </div>
              <div className="dash-card-body">
                <h2>{loading ? "..." : `${dashboardStats.totalOrders}+`}</h2>
              </div>
            </div>
            <div className="dash-card">
              <div className="dash-card-header">
                <span>
                  <i className="fas fa-leaf"></i>
                  <h2>Harvest Yeild</h2>
                </span>

              </div>
              <div className="dash-card-body">
                <h2>{loading ? "..." : `N${dashboardStats.totalExpenses.toLocaleString()}`}</h2>
              </div>
            </div>
            <div className="dash-card">
              <div className="dash-card-header">
                <span>
                  <i className="fas fa-dollar-sign"></i>
                  <h2>Total Income</h2>
                </span>

              </div>
              <div className="dash-card-body">
                <h2>{loading ? "..." : `N${dashboardStats.totalIncome.toLocaleString()}`}</h2>
              </div>
            </div>
          </div>
          <WeatherWidget />
        </div>
        <Calendar initialEvents={farmEvents} />
      </div>
      <div className="bottom-sectio">
        <div className="listed-products-section">
          <div className="list-header">
            <h1>My Produce</h1>
            <Link to={'/farmer-dashboard/produce'}>View All</Link>
          </div>
          <div className="pr-list">
            {loading ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>Loading produce...</p>
              </div>
            ) : error ? (
              <div style={{ textAlign: 'center', padding: '20px', color: 'red' }}>
                <p>{error}</p>
              </div>
            ) : produce.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '20px' }}>
                <p>No produce found. <Link to="/farmer-dashboard/sell">Add your first product</Link></p>
              </div>
            ) : (
              produce.slice(0, 6).map((product) => {
                const stages = getStages(product.growing_stages);
                return (
                  <div key={product.id} className="pr-t">
                    <div className="top-pr">
                      <span>
                        <img src={getProductImage(product.image)} alt={product.name} />
                        <p>{product.name}</p>
                      </span>
                      <i
                        className="fas fa-ellipsis"
                        id={`toggle-${product.id}`}
                        onClick={(e) => toggleProductMenu(product.id, e)}
                      ></i>
                      {activeMenu === product.id && (
                        <ProductMenu productId={product.id} />
                      )}
                    </div>

                    <div className="stages-container-ov">
                      <div className="stage-item-ov">
                        <span className="stage-label-ov">Previous:</span>
                        <span className="stage-value prev-stage-ov">{stages.prevStage}</span>
                      </div>
                      <ChevronRight size={16} className="stage-arrow" />
                      <div className="stage-item-ov">
                        <span className="stage-label-ov">Current:</span>
                        <span className="stage-value-ov current-stage-ov">{stages.currentStage}</span>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </>
  );
}